import { FormEvent, useCallback, useMemo } from "react";
import {
  Stack,
  TextInput,
  Button,
  Group,
  Alert,
  Modal,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  PortalAccessGrant,
  PortalNotificationPreference,
  UpdateGrantInput,
} from "../../../services/admin/portalAccess.service";
import {
  useUpdatePortalAccessGrant,
  usePortalAccessGrantsForSubject,
} from "../../../hooks/admin/usePortalAccess";
import {
  compose,
  required,
  email,
  scrollToFirstError,
} from "../../../utils/formValidation";
import {
  classifyPortalGrantError,
  EDIT_GRANT_ERROR_FALLBACKS,
} from "../../../utils/portalAccessErrors";
import { PortalNotificationPreferenceSelect } from "./PortalNotificationPreferenceSelect";

interface EditPortalGrantFormValues {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  notificationPreference: PortalNotificationPreference;
}

interface EditPortalGrantFormProps {
  grant: PortalAccessGrant;
  onClose: () => void;
}

export function EditPortalGrantForm({
  grant,
  onClose,
}: EditPortalGrantFormProps) {
  const { data: siblingGrants } = usePortalAccessGrantsForSubject(
    grant.subjectType,
    grant.subjectId
  );

  const isPrimary = useMemo(() => {
    if (!siblingGrants || siblingGrants.length === 0) {
      return false;
    }
    const active = siblingGrants
      .filter((g) => g.state !== "revoked")
      .sort(
        (a, b) =>
          new Date(a.invitedAt).getTime() - new Date(b.invitedAt).getTime()
      );
    return active.length > 0 && active[0].id === grant.id;
  }, [siblingGrants, grant.id]);

  const form = useForm<EditPortalGrantFormValues>({
    initialValues: {
      email: grant.portalIdentity.email,
      firstName: grant.firstName ?? grant.portalIdentity.firstName ?? "",
      lastName: grant.lastName ?? grant.portalIdentity.lastName ?? "",
      role: grant.role ?? "",
      notificationPreference: grant.notificationPreference,
    },
    validate: {
      email: compose(required("Email is required"), email()),
      firstName: required("First name is required"),
      lastName: required("Last name is required"),
    },
    validateInputOnBlur: true,
  });

  const updateGrant = useUpdatePortalAccessGrant();

  const handleSubmit = useCallback(
    (values: EditPortalGrantFormValues) => {
      const payload: UpdateGrantInput = {
        firstName: values.firstName.trim() || null,
        lastName: values.lastName.trim() || null,
        role: isPrimary ? undefined : values.role.trim() || null,
        email:
          values.email.trim() !== grant.portalIdentity.email
            ? values.email.trim()
            : undefined,
        notificationPreference: values.notificationPreference,
      };
      updateGrant.mutate(
        { grantId: grant.id, input: payload },
        {
          onSuccess: () => {
            notifications.show({
              color: "green",
              message: "Portal access updated",
            });
            onClose();
          },
          onError: (error) => {
            notifications.show({
              color: "red",
              message: classifyPortalGrantError(
                error,
                EDIT_GRANT_ERROR_FALLBACKS
              ),
            });
          },
        }
      );
    },
    [grant, updateGrant, onClose, isPrimary]
  );

  const handleValidationError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      form.onSubmit(handleSubmit, handleValidationError)(event);
    },
    [form, handleSubmit, handleValidationError]
  );

  const errorMessage = useMemo(
    () =>
      updateGrant.error
        ? classifyPortalGrantError(
            updateGrant.error,
            EDIT_GRANT_ERROR_FALLBACKS
          )
        : null,
    [updateGrant.error]
  );

  return (
    // The form spans Modal.Body and the footer, so it has to carry the flex
    // column through — otherwise Modal.Content scrolls as a whole and takes
    // the footer buttons with it.
    <form
      onSubmit={handleFormSubmit}
      style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <Modal.Body style={{ flex: 1, overflow: "auto" }}>
        <Stack gap="md">
          <TextInput
            label="Email"
            placeholder="person@example.com"
            withAsterisk
            {...form.getInputProps("email")}
          />
          <Group grow>
            <TextInput
              label="First name"
              withAsterisk
              {...form.getInputProps("firstName")}
            />
            <TextInput
              label="Last name"
              withAsterisk
              {...form.getInputProps("lastName")}
            />
          </Group>
          <TextInput
            label="Role"
            placeholder="e.g. Office Manager, PA, Self"
            disabled={isPrimary}
            description={
              isPrimary ? "The primary contact's role is fixed." : undefined
            }
            {...form.getInputProps("role")}
          />
          <PortalNotificationPreferenceSelect
            {...form.getInputProps("notificationPreference")}
          />
          {errorMessage && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Couldn't update grant"
              role="alert"
            >
              {errorMessage}
            </Alert>
          )}
        </Stack>
      </Modal.Body>

      <Divider />
      <Group justify="space-between" p="md">
        <Button variant="default" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button type="submit" loading={updateGrant.isPending}>
          Save changes
        </Button>
      </Group>
    </form>
  );
}
